import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { assets } from '../constants/theme';

const coffeeGrades = [
  {
    id: 'arabica-a',
    badge: 'Specialty Grade',
    badgeClass: 'badge-gold',
    name: 'Arabica A Grade',
    origin: 'Kenyan Highlands',
    altitude: '1,200 – 2,200m',
    score: '82+ SCA',
    price: '$14 – $18',
    description: 'Our signature Arabica A Grade embodies the best of Kenyan highland cultivation — larger beans, exceptional consistency, and a complex flavor that rewards the discerning palate.',
    notes: ['Chocolate', 'Caramel', 'Citrus Hints', 'Floral'],
    brew: 'Best for: Pour Over, French Press, Espresso',
    img: assets.featureLeft,
  },
  {
    id: 'arabica-b',
    badge: 'Daily Specialty',
    badgeClass: 'badge-cream',
    name: 'Arabica B Grade',
    origin: 'Kenyan & West African',
    altitude: '1,000 – 2,000m',
    score: '80+ SCA',
    price: '$11 – $15',
    description: 'Approachable, reliable, and full-bodied — Arabica B Grade is your everyday specialty coffee. Perfect for those who seek consistent quality with rich character in every cup.',
    notes: ['Chocolate', 'Brown Sugar', 'Spice', 'Subtle Fruit'],
    brew: 'Best for: Drip, AeroPress, Moka Pot',
    img: assets.featureRight,
  },
  {
    id: 'kenyan-aa',
    badge: 'Premium Flagship',
    badgeClass: 'badge-dark',
    name: 'Kenyan AA',
    origin: 'Mt. Kenya Slopes',
    altitude: '1,400 – 2,200m',
    score: '84–88 SCA',
    price: '$16 – $22',
    description: 'The crown jewel of our selection. Kenya AA — the largest bean in Kenya\'s prestigious grading system — delivers an unrivaled wine-like body, bright lively acidity, and a complex blackcurrant-forward flavor profile that defines African specialty coffee.',
    notes: ['Blackcurrant', 'Blueberry', 'Jasmine', 'Dark Chocolate', 'Citrus Zest'],
    brew: 'Best for: Pour Over, Chemex, AeroPress',
    img: assets.imgBrew,
  }
];

export default function CoffeeTypes() {
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('fadeIn');
      });
    }, { threshold: 0.06 });

    document.querySelectorAll('.ct-fade').forEach((el, i) => {
      el.style.transitionDelay = `${i * 0.1}s`;
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const s = {
    section: { padding: '8rem 6%', backgroundColor: '#FAF7F2' },
    header: { textAlign: 'center', marginBottom: '5rem' },
    label: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.75rem', fontWeight: 700,
      letterSpacing: '0.18em', textTransform: 'uppercase',
      color: '#D96D29', marginBottom: '1rem',
    },
    title: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: 'clamp(2.2rem, 4vw, 3.2rem)',
      color: '#1C1209', lineHeight: 1.15, marginBottom: '1rem',
    },
    subtitle: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '1rem', color: '#6B4226', lineHeight: 1.7,
      maxWidth: '560px', margin: '0 auto',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '2rem',
      maxWidth: '1240px',
      margin: '0 auto',
    },
    card: {
      backgroundColor: '#fff',
      border: '1px solid #EDE5DC',
      overflow: 'hidden',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    },
    imgWrap: { position: 'relative', aspectRatio: '4/3', overflow: 'hidden' },
    img: { width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s ease' },
    imgOverlay: {
      position: 'absolute', inset: 0,
      background: 'linear-gradient(to top, rgba(28,18,9,0.5) 0%, transparent 60%)',
    },
    badgeWrap: { position: 'absolute', top: '1rem', left: '1rem' },
    cardBody: { padding: '1.75rem' },
    origin: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.12em',
      textTransform: 'uppercase', color: '#A0693A', marginBottom: '0.5rem',
    },
    cardTitle: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: '1.7rem', color: '#1C1209',
      marginBottom: '0.75rem', lineHeight: 1.15,
    },
    desc: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.88rem', color: '#4A2C1A',
      lineHeight: 1.7, marginBottom: '1rem',
    },
    notesRow: { display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1rem' },
    meta: {
      display: 'grid', gridTemplateColumns: '1fr 1fr',
      gap: '0.75rem', borderTop: '1px solid #EDE5DC',
      paddingTop: '1rem', marginBottom: '1.25rem',
    },
    metaItem: { display: 'flex', flexDirection: 'column', gap: '0.2rem' },
    metaLabel: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: '#B08060',
    },
    metaVal: {
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.85rem', fontWeight: 600, color: '#1C1209',
    },
    learnMore: {
      display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
      fontFamily: "'Inter', system-ui, sans-serif",
      fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: '#A63C1E', textDecoration: 'none',
      transition: 'gap 0.2s ease',
    },
    footer: { textAlign: 'center', marginTop: '4rem' },
  };

  return (
    <section id="coffee-types" style={s.section}>
      <div style={s.header}>
        <div className="ct-fade preFade" style={s.label}>Coffee Education</div>
        <h2 className="ct-fade preFade" style={s.title}>
          Explore Our Specialty Grades
        </h2>
        <p className="ct-fade preFade" style={s.subtitle}>
          From bright everyday Arabica to our flagship Kenyan AA — each grade
          carries a unique story, a unique flavor, and a unique impact.
        </p>
      </div>

      <div className="coffee-types-grid ct-fade preFade" style={s.grid}>
        {coffeeGrades.map((g) => (
          <div key={g.id} style={s.card}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-6px)';
              e.currentTarget.style.boxShadow = '0 20px 50px rgba(0,0,0,0.12)';
              const img = e.currentTarget.querySelector('img');
              if (img) img.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
              const img = e.currentTarget.querySelector('img');
              if (img) img.style.transform = 'scale(1)';
            }}
          >
            <div style={s.imgWrap}>
              <img src={g.img} alt={g.name} style={s.img} loading="lazy" />
              <div style={s.imgOverlay} />
              <div style={s.badgeWrap}>
                <span className={`badge ${g.badgeClass}`}>{g.badge}</span>
              </div>
            </div>
            <div style={s.cardBody}>
              <div style={s.origin}>{g.origin}</div>
              <h3 style={s.cardTitle}>{g.name}</h3>
              <p style={s.desc}>{g.description}</p>
              
              <div style={s.notesRow}>
                {g.notes.map(n => <span key={n} className="tasting-tag">{n}</span>)}
              </div>

              <div style={s.meta}>
                <div style={s.metaItem}>
                  <span style={s.metaLabel}>Altitude</span>
                  <span style={s.metaVal}>{g.altitude}</span>
                </div>
                <div style={s.metaItem}>
                  <span style={s.metaLabel}>Cupping Score</span>
                  <span style={s.metaVal}>{g.score}</span>
                </div>
                <div style={s.metaItem}>
                  <span style={s.metaLabel}>Price Range</span>
                  <span style={s.metaVal}>{g.price}</span>
                </div>
                <div style={s.metaItem}>
                  <span style={s.metaLabel}>Best Method</span>
                  <span style={s.metaVal}>Pour Over</span>
                </div>
              </div>

              <Link to={`/coffee-types#${g.id}`} style={s.learnMore}
                onMouseEnter={e => e.currentTarget.style.gap = '0.65rem'}
                onMouseLeave={e => e.currentTarget.style.gap = '0.4rem'}
              >
                Deep Dive →
              </Link>
            </div>
          </div>
        ))}
      </div>

      <div style={s.footer}>
        <Link to="/coffee-types" className="btn-secondary">
          Full Coffee Education Hub →
        </Link>
      </div>
    </section>
  );
}
